import type { getETFDetailContentResponse } from '#/api/generated/content/content';
import { useGetETFDetailContent } from '#/api/generated/content/content';
import type { ContentPage } from '#/api/generated/fINRAMarketDataAPI.schemas';
import type {
  ContentArticleCard,
  DefinitionTerm,
  StaticFallback,
} from '#/content/lib';
import {
  contentArticleCards,
  contentText,
  isContentPage,
  normalizeDefinitions,
} from '#/content/lib';
import { CONTENT_CACHE_QUERY_POLICY } from '#/content/queries';
import { shouldUseFallbackContent } from '#/lib/api-client';

/**
 * The etf-detail content document: view model, fallback copy, merge, and hook.
 * Section intros are Drupal rich text destined for `RichText`, so they carry
 * their HTML through unstripped. Glossary terms (tooltips + stat descriptions)
 * are returned separately as a slug lookup — see {@link normalizeDefinitions}.
 */
export interface ETFDetailContentView {
  riskIntro: string;
  portfolioIntro: string;
  topHoldingsIntro: string;
  priceIntro: string | null;
  learnMoreArticles: ContentArticleCard[] | null;
}

/**
 * Tier 1 fallback etf-detail copy, never null
 */
export const ETF_DETAIL_FALLBACK: StaticFallback<ETFDetailContentView> = {
  riskIntro:
    'These risk metrics are provided to contribute to a fuller picture of the risk of the security. Note that historical performance does not necessarily indicate future performance.',
  portfolioIntro:
    "An ETF's portfolio composition reveals how closely it tracks its underlying index through the allocation of assets across different asset classes and sectors. This breakdown helps illustrate the fund's risk and its alignment with investment goals.",
  topHoldingsIntro:
    'These investment products are the top holdings of this ETF. They are provided as informational, and may be part of an investment strategy for portfolio diversification.',
};

/**
 * Merge fields and fallbacks, null is the total-failure state.
 *
 * The risk intro reads `risk_section_intro_f` — the fund-specific field —
 * never `risk_section_intro`, which belongs to the equity detail document.
 */
export function mergeETFDetail(
  page: ContentPage | null | undefined,
): ETFDetailContentView {
  return {
    riskIntro:
      contentText(page?.risk_section_intro_f) ?? ETF_DETAIL_FALLBACK.riskIntro,
    portfolioIntro:
      contentText(page?.portfolio_section_intro) ??
      ETF_DETAIL_FALLBACK.portfolioIntro,
    topHoldingsIntro:
      contentText(page?.top_holdings_section_intro) ??
      ETF_DETAIL_FALLBACK.topHoldingsIntro,
    priceIntro: contentText(page?.price_cost_section_intro),
    learnMoreArticles: contentArticleCards(page, 'learn_more_articles'),
  };
}

interface UseETFDetailContentResult {
  content: ETFDetailContentView;
  /** Glossary terms for the page, keyed by slug (empty on failure). */
  definitions: Map<string, DefinitionTerm>;
  /** True when the content API failed and fallback copy is being shown. */
  isDegraded: boolean;
}

interface ETFDetailContentData {
  content: ETFDetailContentView;
  definitions: Map<string, DefinitionTerm>;
}

/**
 * Stable fallback returned while the query is unsettled or failed. Reused by
 * reference so the detail view's seed effect doesn't loop on a fresh Map.
 */
const FALLBACK_CONTENT_DATA: ETFDetailContentData = {
  content: mergeETFDetail(null),
  definitions: normalizeDefinitions(null),
};

function selectETFDetailContent(
  response: getETFDetailContentResponse,
): ETFDetailContentData {
  const page = isContentPage(response.data) ? response.data : null;
  return {
    content: mergeETFDetail(page),
    definitions: normalizeDefinitions(page),
  };
}

/**
 * ETF-detail content, merged against fallback copy.
 *
 * Warmed via `etfDetailContentOptions()` on `/$category/$id` with
 * `staleTime: Infinity` for no re-renders for the life of the session.
 */
export function useETFDetailContent(): UseETFDetailContentResult {
  const query = useGetETFDetailContent({
    query: {
      ...CONTENT_CACHE_QUERY_POLICY,
      select: selectETFDetailContent,
    },
  });

  const data = query.isSuccess ? query.data : FALLBACK_CONTENT_DATA;

  return {
    content: data.content,
    definitions: data.definitions,
    isDegraded: shouldUseFallbackContent(query.error),
  };
}
